import { resolveDecimals as sharedResolveDecimals, tickDecimals } from './decimalPrecision';
export { tickDecimals };

export interface InstrumentLike {
  exchange?: string | null;
  decimalPrecision?: number | null;
  tickSize?: number | null;
}


/**
 * The SECOND resolver of this name is retired: this now delegates to `utils/decimalPrecision.ts`,
 * which is the one rule.
 *
 * Both copies carried the same defect twice over. First the stored `decimalPrecision` won
 * outright, so the DELTA import's blanket 8 rendered BTCUSD -- tick 0.5 -- as `80000.00000000`.
 * Then both were given a tick CEILING, which fixed that and left the opposite error untouched: a
 * stored value COARSER than the tick was still trusted, so AKEUSD on a 1e-7 tick displayed
 * `0.02`. Keeping two implementations in step by hand is what let the rule drift twice.
 */
export function resolveDecimals(instr?: InstrumentLike | null): number {
  return sharedResolveDecimals(instr ?? null);
}
function isFiniteNumber(v: any): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export const fmt = {
  price(value: number | null | undefined, instr?: InstrumentLike | null): string {
    if (!isFiniteNumber(value)) return '—';
    const d = resolveDecimals(instr);
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  },

  qty(value: number | null | undefined, instr?: InstrumentLike | null): string {
    if (!isFiniteNumber(value)) return '—';
    const ex = (instr?.exchange || '').toUpperCase();
    const isCrypto = ex === 'DELTA';
    const d = isCrypto ? Math.min(4, resolveDecimals(instr)) : 0;
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: d,
    });
  },

  pct(value: number | null | undefined, decimals = 2): string {
    if (!isFiniteNumber(value)) return '—';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(decimals)}%`;
  },

  inr(value: number | null | undefined, decimals = 2): string {
    if (!isFiniteNumber(value)) return '—';
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  },

  num(value: number | null | undefined, decimals = 2): string {
    if (!isFiniteNumber(value)) return '—';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  },

  abbrev(value: number | null | undefined): string {
    if (!isFiniteNumber(value)) return '—';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`;
    return `${sign}${abs.toFixed(2)}`;
  },
};

export default fmt;
